import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { setTokens } from '../lib/utils'

interface VerifyEmailPageProps {
  onLogin?: () => void
}

export default function VerifyEmailPage({ onLogin }: VerifyEmailPageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Token de vérification manquant')
        setLoading(false)
        return
      }

      try {
        const verifyResponse = await api.post('/fapi/auth/verify-with-login', { token })
        
        if (verifyResponse.data && verifyResponse.data.access_token) {
          const accessToken = verifyResponse.data.access_token
          setTokens(accessToken, null)
          
          try {
            const userResponse = await api.get('/fapi/users/me', {
              headers: { 'Authorization': `Bearer ${accessToken}` }
            })
            localStorage.setItem('user', JSON.stringify(userResponse.data))
          } catch (userErr) {
            console.error('Error fetching user data:', userErr)
          }
          
          setSuccess(true)
          
          // Attendre 2 secondes pour afficher le message de succès avant la redirection
          await new Promise(resolve => setTimeout(resolve, 2000))
          
          // Forcer un rechargement complet pour garantir que l'état d'authentification est pris en compte
          window.location.href = '/'
        } else {
          setError('Vérification réussie mais impossible de vous connecter automatiquement. Veuillez vous connecter manuellement.')
          setTimeout(() => {
            navigate('/login')
          }, 3000)
        }
      } catch (err: any) {
        const errorDetail = err?.response?.data?.detail
        if (errorDetail === 'VERIFY_USER_BAD_TOKEN') {
          setError('Le lien de vérification est invalide ou a expiré.')
        } else if (errorDetail === 'VERIFY_USER_ALREADY_VERIFIED') {
          setError('Votre email est déjà vérifié. Vous pouvez vous connecter.')
        } else {
          setError(errorDetail || 'Erreur lors de la vérification')
        }
      } finally {
        setLoading(false)
      }
    }

    verifyEmail()
  }, [token, navigate, onLogin])

  if (loading) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-10 px-4 py-12 sm:py-16">
        <div className="relative overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 shadow-2xl backdrop-blur dark:border-emerald-900/40 dark:bg-zinc-950/90">
          <div className="px-8 py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600 dark:border-emerald-800 dark:border-t-emerald-400"></div>
            </div>
            <h2 className="mb-2 text-xl font-semibold text-zinc-900 dark:text-white">Vérification en cours...</h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Veuillez patienter pendant que nous vérifions votre email.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-10 px-4 py-12 sm:py-16">
        <div className="relative overflow-hidden rounded-3xl border border-red-100/80 bg-white/90 shadow-2xl backdrop-blur dark:border-red-900/40 dark:bg-zinc-950/90">
          <div className="px-8 py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50">
              <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mb-2 text-xl font-semibold text-zinc-900 dark:text-white">Erreur de vérification</h2>
            <p className="mb-6 text-sm text-zinc-600 dark:text-zinc-400">
              {error}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
            >
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-10 px-4 py-12 sm:py-16">
      <div className="relative overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 shadow-2xl shadow-emerald-500/10 backdrop-blur dark:border-emerald-900/40 dark:bg-zinc-950/90 dark:shadow-none">
        <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-500/15 via-emerald-400/10 to-transparent" />
        <div className="relative px-8 py-12 sm:px-12">
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50">
              <svg className="h-8 w-8 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">Email vérifié !</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Votre adresse email a été vérifiée avec succès.
              </p>
              <div className="flex flex-col items-center gap-3 pt-2">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600 dark:border-emerald-800 dark:border-t-emerald-400"></div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Connexion automatique en cours...
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Redirection vers le tableau de bord
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
